import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  TrendingUp, Users, Target, CheckCircle2, ArrowUpRight, ArrowDownRight, 
  Link2, FileText, Bot, BarChart3, Sparkles, Eye, 
  Clock, FileCheck, AlertCircle, Edit3, Save, X, Building2, Trophy, Plus, Globe, Activity, Lock,
  DollarSign, Share2, Video, Globe2, Languages, Award
} from 'lucide-react';
import GuidedSetup, { PreviewBanner, LockedSection, CompetitorGapAlert, PostUpgradeCallPrompt } from './GuidedSetup';
import AccessPendingPanel from './AccessPendingPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Overview({ user, selectedClient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gaData, setGaData] = useState(null);
  const [gaLoading, setGaLoading] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [editingSteps, setEditingSteps] = useState(false);
  const [strategicSteps, setStrategicSteps] = useState([]);
  const [newStep, setNewStep] = useState('');
  const [activityLogs, setActivityLogs] = useState([]);
  const [setupStatus, setSetupStatus] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [execSummary, setExecSummary] = useState(null);
  
  // Competitor Suggestion Modal State (for Clients)
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestionData, setSuggestionData] = useState({ competitor_name: '', competitor_website: '' });
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  const firmId = selectedClient?.firmId || user?.firm_id;
  const canEdit = user?.role && ['super_admin', 'internal_team'].includes(user.role);
  const isClient = user?.role === 'client';

  useEffect(() => {
    if (firmId) {
      fetchOverviewData();
      fetchAIVisibilityData();
      fetchActivityLogs();
      fetchSetupStatus();
      fetchExecSummary();
    }
  }, [firmId]);

  useEffect(() => {
    if (data?.firm?.ga_connected && firmId) {
      fetchGAData();
    }
  }, [firmId, data?.firm?.ga_connected]);

  const fetchSetupStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/setup-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupStatus(response.data);
    } catch (err) {
      console.error('Error fetching setup status:', err);
    }
  };

  const fetchExecSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = firmId
        ? `${API}/dashboard/executive-summary?firm_id=${firmId}`
        : `${API}/dashboard/executive-summary`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExecSummary(response.data);
    } catch (err) {
      console.error('Error fetching executive summary:', err);
    }
  };

  const fetchOverviewData = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedClient 
        ? `${API}/admin/client-dashboard/${firmId}`
        : `${API}/dashboard/overview`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setStrategicSteps(response.data.strategic_next_steps || []);
    } catch (err) {
      console.error('Error fetching overview:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch AI Visibility data from Position Tracking (source of truth)
  const fetchAIVisibilityData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ai-visibility?firm_id=${firmId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Get the latest upload
      if (response.data && response.data.length > 0) {
        setAiData(response.data[0]);
      }
    } catch (err) {
      console.error('Error fetching AI visibility data:', err);
    }
  };

  // Fetch GA data from Traffic Analytics (same data source)
  const fetchGAData = async () => {
    setGaLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/traffic/${firmId}?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGaData(response.data);
    } catch (err) {
      console.error('Error fetching GA data:', err);
    } finally {
      setGaLoading(false);
    }
  };

  // Fetch Activity Logs
  const fetchActivityLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedClient 
        ? `${API}/activity-logs/${firmId}?limit=10`
        : `${API}/activity-logs?limit=10`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityLogs(response.data || []);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
    }
  };

  const saveStrategicSteps = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/strategic-next-steps/${firmId}`, strategicSteps, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setEditingSteps(false);
    } catch (err) {
      console.error('Error saving strategic steps:', err);
      alert('Failed to save strategic steps');
    }
  };

  const addStep = () => {
    if (newStep.trim()) {
      setStrategicSteps([...strategicSteps, newStep.trim()]);
      setNewStep('');
    }
  };

  const removeStep = (index) => {
    setStrategicSteps(strategicSteps.filter((_, i) => i !== index));
  };

  // Submit competitor suggestion (for clients)
  const submitCompetitorSuggestion = async () => {
    if (!suggestionData.competitor_name.trim()) {
      alert('Please enter a competitor name');
      return;
    }
    
    setSubmittingSuggestion(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/competitor-suggestions`, suggestionData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      setShowSuggestModal(false);
      setSuggestionData({ competitor_name: '', competitor_website: '' });
      alert('Thank you! Your competitor suggestion has been submitted to the team for review.');
    } catch (err) {
      console.error('Error submitting suggestion:', err);
      alert('Failed to submit suggestion. Please try again.');
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-slate-500">Unable to load dashboard data</div>
    );
  }

  const workCompleted = data.work_completed || {};
  const pendingApprovals = data.pending_approvals || {};
  
  // Get Total Citations from AI Visibility data (Position Tracking source)
  const totalCitations = aiData?.aggregate_metrics?.total_citations || 0;
  const aiVisibilityScore = aiData?.aggregate_metrics?.prompt_coverage_pct || 0;
  const lastUpdated = aiData?.upload_date;
  
  // Get top 4 prompts from prompt_metrics
  const topPrompts = (aiData?.prompt_metrics || []).slice(0, 4);
  
  // Get GA metrics (same as Traffic Analytics)
  const gaAggregate = gaData?.data?.aggregate || {};

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  // Format activity date
  const formatActivityDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get activity icon based on type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'backlinks_added':
        return Link2;
      case 'blog_uploaded':
        return FileText;
      case 'keywords_uploaded':
        return Target;
      case 'ai_visibility_updated':
        return Bot;
      case 'technical_update':
        return CheckCircle2;
      case 'report_generated':
        return BarChart3;
      default:
        return Activity;
    }
  };

  // Check if user is in preview mode (not subscribed)
  const isPreview = setupStatus?.is_preview && isClient;
  // For unpaid/no-plan clients we now drive upgrades from the dashboard-wide
  // NoPlanBanner - hide the guided setup + preview cards so the empty state
  // stays clean.
  const showGuidedSetup = isClient && !setupStatus?.setup_complete && !isPreview;

  // Get competitor data for gap alert
  const competitors = aiData?.competitors || [];
  const topCompetitorCitations = competitors.length > 0 
    ? Math.max(...competitors.map(c => c.total_citations || 0))
    : 0;

  return (
    <div className="space-y-8 animate-fadeInUp" data-testid="overview-page">
      {/* Guided Setup Card - Show for new clients */}
      {showGuidedSetup && (
        <GuidedSetup 
          user={user} 
          firmId={firmId} 
          onSetupComplete={fetchSetupStatus}
        />
      )}

      {/* Preview Banner - suppressed: replaced by dashboard-wide NoPlanBanner */}
      {false && isPreview && (
        <PreviewBanner onUpgrade={() => setShowUpgradePrompt(true)} />
      )}

      {/* Pending access items needing the client's action */}
      {isClient && firmId && <AccessPendingPanel firmId={firmId} />}

      {/* Competitor Gap Alert - Show when there's a gap */}
      {isClient && totalCitations > 0 && topCompetitorCitations > totalCitations && (
        <CompetitorGapAlert 
          yourCitations={totalCitations} 
          topCompetitorCitations={topCompetitorCitations}
        />
      )}

      {/* Post-Upgrade Call Prompt */}
      {showUpgradePrompt && <PostUpgradeCallPrompt />}

      {/* Header */}
      <div className="opacity-0 animate-fadeInUp" style={{animationDelay: '0s', animationFillMode: 'forwards'}}>
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
          Executive Dashboard
        </h1>
        <p className="text-slate-500 text-lg">AI-first performance overview for {data.firm?.name || 'your firm'}</p>
      </div>

      {/* SECTION 1: TOTAL CITATIONS - Always visible (even in preview) */}
      <div className="opacity-0 animate-fadeInUp" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Total Citations</h2>
              <p className="text-sm text-slate-500">
                Last Updated: {formatDate(lastUpdated)}
              </p>
            </div>
          </div>
        </div>

        {/* Total Citations Card */}
        <div
          className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-emerald-500 to-teal-500 text-white hover-lift soft-shadow-lg"
          data-testid="ai-kpi-total-citations"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-emerald-500/30">
                <Bot className="w-5 h-5" />
              </div>
            </div>
            <div className="text-4xl font-bold mb-1" style={{fontFamily: 'Outfit'}}>{totalCitations}</div>
            <div className="text-white/80 text-sm font-medium">Total AI Citations</div>
            <div className="text-white/60 text-xs mt-1">ChatGPT + Perplexity + Gemini</div>
          </div>
        </div>
      </div>

      {/* SECTION 1.5: PERFORMANCE SNAPSHOT - consolidated executive view pulling from every module */}
      {execSummary && (
        <ExecutiveSnapshot data={execSummary} isPreview={isPreview} />
      )}

      {/* SECTION 2: TOP PROMPTS YOU APPEAR IN - Locked in preview */}
      {isPreview ? (
        <LockedSection 
          title="Top Prompts You Appear In" 
          description="Upgrade to see which AI prompts mention your business"
        />
      ) : (
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow opacity-0 animate-fadeInUp" style={{animationDelay: '0.15s', animationFillMode: 'forwards'}} data-testid="top-prompts">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Eye className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Top Prompts You Appear In</h2>
        </div>
        
        {topPrompts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Prompt</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Platform</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Position / Rank</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Citation Visibility %</th>
                </tr>
              </thead>
              <tbody>
                {topPrompts.map((prompt, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="py-3 px-4 text-sm text-slate-800 max-w-md truncate">{prompt.prompt}</td>
                    <td className="py-3 px-4">
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">
                        {prompt.platform}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        #{prompt.position || idx + 1}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-sm font-semibold text-slate-700">
                        {prompt.visibility_score_pct?.toFixed(1) || 0}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Bot className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No prompt visibility data available yet.</p>
          </div>
        )}
      </div>
      )}

      {/* SECTION 3: COMPETITIVE AI VISIBILITY ANALYSIS - Locked in preview */}
      {isPreview ? (
        <LockedSection 
          title="Competitive AI Visibility" 
          description="Upgrade to compare your visibility against competitors"
        />
      ) : (
      <CompetitiveAnalysis 
        aiData={aiData} 
        totalCitations={totalCitations} 
        firmName={data.firm?.name || 'Your Firm'}
        isClient={isClient}
        onSuggestCompetitor={() => setShowSuggestModal(true)}
      />
      )}

      {/* SECTION 4: ORGANIC PERFORMANCE - Mirrored from Traffic Analytics GA Data */}
      <div className="opacity-0 animate-fadeInUp" style={{animationDelay: '0.25s', animationFillMode: 'forwards'}}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Organic Performance</h2>
        </div>

        {data.firm?.ga_connected ? (
          gaLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[1,2,3].map(i => (
                <div key={i} className="bg-white/70 rounded-2xl p-6 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-20 mb-3"></div>
                  <div className="h-8 bg-slate-200 rounded w-16"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Sessions - Same as Traffic Analytics */}
              <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-emerald-500 to-teal-500 text-white hover-lift soft-shadow-lg">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6"></div>
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-emerald-500/30 mb-3">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div className="text-3xl font-bold mb-1" style={{fontFamily: 'Outfit'}}>
                    {gaAggregate.sessions?.toLocaleString() || '0'}
                  </div>
                  <div className="text-white/80 text-sm font-medium">Sessions</div>
                </div>
              </div>

              {/* Total Users */}
              <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-violet-500 to-indigo-600 text-white hover-lift soft-shadow-lg">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6"></div>
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-violet-500/30 mb-3">
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="text-3xl font-bold mb-1" style={{fontFamily: 'Outfit'}}>
                    {gaAggregate.totalUsers?.toLocaleString() || '0'}
                  </div>
                  <div className="text-white/80 text-sm font-medium">Total Users</div>
                </div>
              </div>

              {/* Page Views */}
              <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-amber-400 to-orange-500 text-white hover-lift soft-shadow-lg">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6"></div>
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-amber-500/30 mb-3">
                    <Eye className="w-5 h-5" />
                  </div>
                  <div className="text-3xl font-bold mb-1" style={{fontFamily: 'Outfit'}}>
                    {gaAggregate.screenPageViews?.toLocaleString() || '0'}
                  </div>
                  <div className="text-white/80 text-sm font-medium">Page Views</div>
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow text-center">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-600 font-medium">Connect Google Analytics to view organic performance</p>
            <p className="text-sm text-slate-400 mt-1">Go to Traffic Analytics to connect your GA property</p>
          </div>
        )}
      </div>

      {/* SECTION 5: RECENT ACTIVITY TIMELINE */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow opacity-0 animate-fadeInUp" style={{animationDelay: '0.3s', animationFillMode: 'forwards'}} data-testid="recent-activity">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Recent Activity</h2>
        </div>
        
        {activityLogs.length > 0 ? (
          <div className="space-y-4">
            {activityLogs.map((log, idx) => {
              const IconComponent = getActivityIcon(log.activity_type);
              return (
                <div key={log.id || idx} className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/50 hover:from-slate-100 hover:to-slate-100 transition-all" data-testid={`activity-log-${idx}`}>
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{log.description}</p>
                    <p className="text-xs text-slate-500 mt-1">{formatActivityDate(log.created_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No recent activity to display.</p>
            <p className="text-sm text-slate-400 mt-1">Activity will appear here as work is completed.</p>
          </div>
        )}
      </div>

      {/* SECTION 6 & 7: WORK COMPLETED + PENDING APPROVALS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Work Completed This Month */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow hover-lift opacity-0 animate-fadeInUp" style={{animationDelay: '0.35s', animationFillMode: 'forwards'}} data-testid="work-completed">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Work Completed</h2>
              <p className="text-sm text-slate-500">This month</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-gradient-to-br from-violet-50 to-indigo-50">
              <div className="text-2xl font-bold text-violet-600" style={{fontFamily: 'Outfit'}}>{workCompleted.blogs_created || 0}</div>
              <div className="text-sm text-slate-600">Blogs Created</div>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50">
              <div className="text-2xl font-bold text-emerald-600" style={{fontFamily: 'Outfit'}}>{workCompleted.pages_optimized || 0}</div>
              <div className="text-sm text-slate-600">Pages Optimized</div>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50">
              <div className="text-2xl font-bold text-blue-600" style={{fontFamily: 'Outfit'}}>{workCompleted.backlinks_added || 0}</div>
              <div className="text-sm text-slate-600">Backlinks Added</div>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50">
              <div className="text-2xl font-bold text-amber-600" style={{fontFamily: 'Outfit'}}>{workCompleted.technical_updates || 0}</div>
              <div className="text-sm text-slate-600">Technical Updates</div>
            </div>
          </div>
        </div>

        {/* Pending Approvals */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow hover-lift opacity-0 animate-fadeInUp" style={{animationDelay: '0.4s', animationFillMode: 'forwards'}} data-testid="pending-approvals">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Pending Approvals</h2>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/50">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-violet-500" />
                <span className="font-medium text-slate-700">Blogs</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                pendingApprovals.blogs > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {pendingApprovals.blogs || 0} pending
              </span>
            </div>
            
            <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/50">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-slate-700">FAQs</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                pendingApprovals.faqs > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {pendingApprovals.faqs || 0} pending
              </span>
            </div>
            
            <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/50">
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-emerald-500" />
                <span className="font-medium text-slate-700">Keywords</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                pendingApprovals.keywords > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {pendingApprovals.keywords || 0} pending
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 8: STRATEGIC NEXT STEPS */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow opacity-0 animate-fadeInUp" style={{animationDelay: '0.45s', animationFillMode: 'forwards'}} data-testid="strategic-next-steps">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Strategic Next Steps</h2>
          </div>
          
          {canEdit && (
            <button
              onClick={() => editingSteps ? saveStrategicSteps() : setEditingSteps(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                editingSteps 
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="edit-strategic-steps-btn"
            >
              {editingSteps ? <Save className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
              {editingSteps ? 'Save' : 'Edit'}
            </button>
          )}
        </div>
        
        {editingSteps && canEdit ? (
          <div className="space-y-3">
            {strategicSteps.map((step, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <span className="w-6 h-6 rounded-full bg-violet-500 text-white text-sm flex items-center justify-center font-semibold">
                  {index + 1}
                </span>
                <span className="flex-1 text-slate-700">{step}</span>
                <button
                  onClick={() => removeStep(index)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            <div className="flex items-center gap-3 mt-4">
              <input
                type="text"
                value={newStep}
                onChange={(e) => setNewStep(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addStep()}
                placeholder="Add a new strategic step..."
                className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <button
                onClick={addStep}
                className="px-4 py-3 bg-violet-500 text-white rounded-xl font-medium hover:bg-violet-600"
              >
                Add
              </button>
            </div>
          </div>
        ) : strategicSteps.length > 0 ? (
          <ul className="space-y-3">
            {strategicSteps.map((step, index) => (
              <li key={index} className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                <span className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-white text-sm flex items-center justify-center font-semibold flex-shrink-0">
                  {index + 1}
                </span>
                <span className="text-slate-700">{step}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-6 text-slate-500">
            <Target className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p>No strategic next steps defined yet.</p>
            {canEdit && <p className="text-sm text-slate-400 mt-1">Click Edit to add strategic goals.</p>}
          </div>
        )}
      </div>

      {/* Business Summary - Compact */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow opacity-0 animate-fadeInUp" style={{animationDelay: '0.5s', animationFillMode: 'forwards'}} data-testid="business-summary">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Account Overview</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/50">
            <span className="text-sm text-slate-500">Firm</span>
            <p className="font-semibold text-slate-800 truncate">{data.firm?.name || 'N/A'}</p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/50">
            <span className="text-sm text-slate-500">Plan</span>
            <p className="font-semibold text-violet-600">{data.firm?.subscription_plan || 'N/A'}</p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/50">
            <span className="text-sm text-slate-500">Analytics</span>
            <p className={`font-semibold ${data.firm?.ga_connected ? 'text-emerald-600' : 'text-slate-400'}`}>
              {data.firm?.ga_connected ? 'Connected' : 'Not Connected'}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/50">
            <span className="text-sm text-slate-500">Search Console</span>
            <p className={`font-semibold ${data.firm?.gsc_connected ? 'text-emerald-600' : 'text-slate-400'}`}>
              {data.firm?.gsc_connected ? 'Connected' : 'Not Connected'}
            </p>
          </div>
        </div>
      </div>

      {/* Suggest Competitor Modal - For Clients */}
      {showSuggestModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="suggest-competitor-modal">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 soft-shadow-lg animate-fadeInUp">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Suggest a Competitor</h2>
                <p className="text-sm text-slate-500">Help us track competitors you care about</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Competitor Name *</label>
                <input
                  type="text"
                  value={suggestionData.competitor_name}
                  onChange={(e) => setSuggestionData({ ...suggestionData, competitor_name: e.target.value })}
                  placeholder="e.g., Smith & Associates Law Firm"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="suggest-competitor-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Website (optional)</label>
                <input
                  type="text"
                  value={suggestionData.competitor_website}
                  onChange={(e) => setSuggestionData({ ...suggestionData, competitor_website: e.target.value })}
                  placeholder="e.g., smithlaw.com"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="suggest-competitor-website-input"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-100">
              <button
                onClick={() => {
                  setShowSuggestModal(false);
                  setSuggestionData({ competitor_name: '', competitor_website: '' });
                }}
                className="px-5 py-2.5 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={submitCompetitorSuggestion}
                disabled={submittingSuggestion}
                className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-violet-500/30 transition-all disabled:opacity-50"
                data-testid="submit-competitor-suggestion-btn"
              >
                {submittingSuggestion ? 'Submitting...' : 'Submit Suggestion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Competitive Analysis Component
function CompetitiveAnalysis({ aiData, totalCitations, firmName, isClient, onSuggestCompetitor }) {
  const competitors = aiData?.competitors || [];
  
  // Build comparison data
  const comparisonData = [
    { name: firmName, citations: totalCitations, isYou: true }
  ];
  
  competitors.forEach(c => {
    if (c.name && c.total_citations !== undefined) {
      comparisonData.push({
        name: c.name,
        website: c.website,
        citations: c.total_citations,
        isYou: false
      });
    }
  });
  
  // Sort by citations descending
  comparisonData.sort((a, b) => b.citations - a.citations);
  
  // Find max citations for bar calculation
  const maxCitations = Math.max(...comparisonData.map(d => d.citations), 1);
  
  // Find leader
  const leader = comparisonData[0];
  
  // If no competitor data, show placeholder
  if (competitors.length === 0) {
    return (
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow opacity-0 animate-fadeInUp" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}} data-testid="competitive-analysis-empty">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Competitive AI Visibility</h2>
              <p className="text-sm text-slate-500">Compare your AI presence with competitors</p>
            </div>
          </div>
          {isClient && (
            <button
              onClick={onSuggestCompetitor}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-violet-500/30 transition-all text-sm"
              data-testid="suggest-competitor-btn"
            >
              <Plus className="w-4 h-4" />
              Suggest Competitor
            </button>
          )}
        </div>
        
        <div className="text-center py-8">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No competitor data available yet.</p>
          {isClient && (
            <p className="text-sm text-slate-400 mt-1">Click "Suggest Competitor" to recommend competitors for tracking.</p>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow opacity-0 animate-fadeInUp" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}} data-testid="competitive-analysis">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Competitive AI Visibility</h2>
            <p className="text-sm text-slate-500">Total Citations comparison</p>
          </div>
        </div>
        {isClient && (
          <button
            onClick={onSuggestCompetitor}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-violet-500/30 transition-all text-sm"
            data-testid="suggest-competitor-btn"
          >
            <Plus className="w-4 h-4" />
            Suggest Competitor
          </button>
        )}
      </div>
      
      {/* Comparison Bars */}
      <div className="space-y-4">
        {comparisonData.map((item, idx) => {
          const barWidth = maxCitations > 0 ? (item.citations / maxCitations) * 100 : 0;
          const isLeader = idx === 0;
          
          return (
            <div key={idx} className="relative" data-testid={`competitor-row-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${item.isYou ? 'text-violet-700' : 'text-slate-700'}`}>
                    {item.name}
                  </span>
                  {item.isYou && (
                    <span className="px-2 py-0.5 bg-violet-100 text-violet-600 text-xs font-semibold rounded-full">
                      You
                    </span>
                  )}
                  {isLeader && (
                    <span className="px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      Leader
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {item.website && (
                    <a 
                      href={item.website.startsWith('http') ? item.website : `https://${item.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-400 hover:text-violet-600 flex items-center gap-1"
                    >
                      <Globe className="w-3 h-3" />
                      {item.website}
                    </a>
                  )}
                  <span className="font-bold text-slate-900 text-lg" style={{fontFamily: 'Outfit'}}>
                    {item.citations}
                  </span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="h-8 bg-slate-100 rounded-lg overflow-hidden">
                <div
                  className={`h-full rounded-lg transition-all duration-700 ${
                    item.isYou 
                      ? 'bg-gradient-to-r from-violet-500 to-indigo-600' 
                      : isLeader 
                        ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                        : 'bg-gradient-to-r from-slate-400 to-slate-500'
                  }`}
                  style={{ width: `${barWidth}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Insight Text */}
      {leader && (
        <div className="mt-5 p-4 bg-gradient-to-r from-slate-50 to-slate-100/50 rounded-xl">
          <p className="text-sm text-slate-600">
            {leader.isYou ? (
              <>
                <span className="text-emerald-600 font-semibold">Great news!</span> Your firm is currently leading in AI visibility with {leader.citations} citations.
              </>
            ) : (
              <>
                <span className="font-semibold">{leader.name}</span> is currently leading with {leader.citations} citations. 
                {comparisonData.find(d => d.isYou) && (
                  <> You're at {comparisonData.find(d => d.isYou)?.citations} citations - {leader.citations - (comparisonData.find(d => d.isYou)?.citations || 0)} behind the leader.</>
                )}
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Executive Performance Snapshot - consolidated cards pulling from every module
// ══════════════════════════════════════════════════════════════════════
function ExecutiveSnapshot({ data, isPreview }) {
  const {
    visibility = {}, seo = {}, content = {}, link_building = {},
    paid_ads = {}, social_media = {}, ai_video = {}, website = {}, translations = {}
  } = data || {};

  const currencySymbol = { GBP: '£', EUR: '€', USD: '$' }[paid_ads.currency] || '£';

  return (
    <div className="space-y-5 opacity-0 animate-fadeInUp" style={{ animationDelay: '0.12s', animationFillMode: 'forwards' }} data-testid="executive-snapshot">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
          <Award className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Performance Snapshot</h2>
          <p className="text-sm text-slate-500">Consolidated view across every module - numbers match each tab's source of truth.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SnapshotCard
          testid="snapshot-visibility"
          title="Overall Visibility"
          subtitle="AI & search visibility health"
          icon={Sparkles}
          iconBg="from-violet-500 to-indigo-600"
        >
          <StatRow label="Total AI Citations" value={visibility.total_citations ?? 0} />
          <StatRow label="Prompt Coverage" value={`${(visibility.prompt_coverage_pct || 0).toFixed(1)}%`} />
          <StatRow label="Keywords in Top 10" value={visibility.keywords_in_top_10 ?? 0} />
        </SnapshotCard>

        <SnapshotCard
          testid="snapshot-seo"
          title="SEO Performance"
          subtitle="Last 30 days"
          icon={TrendingUp}
          iconBg="from-emerald-500 to-teal-500"
        >
          <StatRow label="Sessions" value={(seo.sessions || 0).toLocaleString()} />
          <StatRow label="Users" value={(seo.users || 0).toLocaleString()} />
          <StatRow label="Page Views" value={(seo.page_views || 0).toLocaleString()} />
        </SnapshotCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SnapshotCard
          testid="snapshot-content"
          title="Content & Work Delivered"
          subtitle="This month"
          icon={FileText}
          iconBg="from-blue-500 to-cyan-500"
        >
          <StatRow label="Blogs Created" value={content.blogs_this_month ?? 0} />
          <StatRow label="Pages Optimised" value={content.pages_optimized ?? 0} />
          <StatRow label="Metadata Updates" value={content.metadata_updates ?? 0} />
          <StatRow label="FAQs Added" value={content.faqs_added ?? 0} />
        </SnapshotCard>

        <SnapshotCard
          testid="snapshot-links"
          title="Link Building"
          subtitle="Backlink activity"
          icon={Link2}
          iconBg="from-amber-500 to-orange-500"
        >
          <StatRow label="Total Backlinks" value={link_building.total_backlinks ?? 0} />
          <StatRow label="Latest Batch" value={`${link_building.latest_batch ?? 0} links`} />
          {link_building.latest_batch_title && (
            <p className="text-xs text-slate-500 italic mt-1">{link_building.latest_batch_title}</p>
          )}
        </SnapshotCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SnapshotCard
          testid="snapshot-ads"
          title="Paid Ads"
          subtitle="Latest reporting period"
          icon={DollarSign}
          iconBg="from-rose-500 to-pink-600"
        >
          <StatRow label="Spend" value={`${currencySymbol}${(paid_ads.spend || 0).toLocaleString()}`} />
          <StatRow label="Leads" value={paid_ads.leads ?? 0} />
          <StatRow label="Cost per Lead" value={`${currencySymbol}${(paid_ads.cpl || 0).toFixed(2)}`} />
        </SnapshotCard>

        <SnapshotCard
          testid="snapshot-social"
          title="Social Media"
          subtitle="Content pipeline"
          icon={Share2}
          iconBg="from-sky-500 to-blue-600"
        >
          <StatRow label="Posts This Month" value={social_media.posts_this_month ?? 0} />
          <StatRow label="Pending Approvals" value={social_media.pending_approvals ?? 0} />
          <StatRow label="Scheduled" value={social_media.scheduled_posts ?? 0} />
        </SnapshotCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SnapshotCard
          testid="snapshot-video"
          title="AI Video"
          subtitle="Production status"
          icon={Video}
          iconBg="from-fuchsia-500 to-purple-600"
        >
          <StatRow label="In Production" value={ai_video.in_production ?? 0} />
          <StatRow label="Pending Approval" value={ai_video.pending_approval ?? 0} />
          <StatRow label="Delivered" value={ai_video.delivered ?? 0} />
        </SnapshotCard>

        <SnapshotCard
          testid="snapshot-translations"
          title="Legal Translations"
          subtitle="Request pipeline"
          icon={Languages}
          iconBg="from-indigo-500 to-violet-600"
        >
          <StatRow label="Total Requests" value={translations.total ?? 0} />
          <StatRow label="Ready for Download" value={translations.completed ?? 0} />
          <StatRow label="In Review" value={translations.in_review ?? 0} />
          <StatRow label="Submitted" value={translations.submitted ?? 0} />
        </SnapshotCard>
      </div>

      <SnapshotCard
        testid="snapshot-website"
        title="Website Creation"
        subtitle="Current build stage"
        icon={Globe2}
        iconBg="from-teal-500 to-emerald-600"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-slate-600">Current Stage</span>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700" data-testid="snapshot-website-stage">
            {website.stage_label || 'Not Started'}
          </span>
        </div>
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-violet-500 via-indigo-500 to-emerald-500 transition-all duration-700"
            style={{ width: `${Math.max(0, Math.min(100, website.progress_pct || 0))}%` }}
          />
        </div>
        <p className="text-xs text-slate-500 mt-2">{website.progress_pct || 0}% complete</p>
      </SnapshotCard>
    </div>
  );
}

function SnapshotCard({ title, subtitle, icon: Icon, iconBg, children, testid }) {
  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow hover-lift" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${iconBg} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{title}</h3>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{value}</span>
    </div>
  );
}
